<script setup lang="ts">
const currentYear = new Date().getFullYear();

const quickLinks = [
  { path: '/produkte', label: 'Produkte' },
  { path: '/unternehmen', label: 'Unternehmen' },
  { path: '/karriere', label: 'Karriere' },
  { path: '/aktuelles', label: 'Aktuelles' },
  { path: '/kontakt', label: 'Kontakt' }
];

const productCategories = [
  { path: '/produkte#filterzellen', label: 'Filterzellen' },
  { path: '/produkte#panelfilter', label: 'Panelfilter' },
  { path: '/produkte#rollenware', label: 'Rollenware' },
  { path: '/produkte#taschenfilter', label: 'Taschenfilter' }
];
</script>

<template>
  <footer class="footer">
    <div class="footer-main">
      <div class="container">
        <div class="footer-grid">
          <!-- Company Info -->
          <div class="footer-section footer-brand">
            <RouterLink to="/" class="footer-logo">
              <img src="/mr-logo.svg" alt="M&R Filtermedien" class="footer-logo-img" width="287" height="287" />
              <span class="footer-logo-label">M&R Filtermedien GmbH</span>
            </RouterLink>
            <p class="footer-tagline">
              Ihr zuverlässiger Partner für hochwertige Filtermedien.
              Qualität und Innovation aus Deutschland.
            </p>
          </div>

          <!-- Quick Links -->
          <div class="footer-section">
            <h4 class="footer-title">Navigation</h4>
            <ul class="footer-links">
              <li v-for="link in quickLinks" :key="link.path">
                <RouterLink :to="link.path">{{ link.label }}</RouterLink>
              </li>
            </ul>
          </div>

          <!-- Products -->
          <div class="footer-section">
            <h4 class="footer-title">Produkte</h4>
            <ul class="footer-links">
              <li v-for="category in productCategories" :key="category.path">
                <RouterLink :to="category.path">{{ category.label }}</RouterLink>
              </li>
            </ul>
          </div>

          <!-- Contact -->
          <div class="footer-section">
            <h4 class="footer-title">Kontakt</h4>
            <address class="footer-contact">
              <p>
                M&R Filtermedien GmbH<br>
                Prenzlauer Straße 68<br>
                16348 Wandlitz OT Basdorf
              </p>
              <p>
                <a href="tel:+493****8700">+49 33397 298-700</a><br>
                <a href="mailto:info@mr-filtermedien.de">info@mr-filtermedien.de</a>
              </p>
              <p class="footer-hours">
                Mo–Fr: 6:00 – 15:00 Uhr
              </p>
            </address>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Bottom -->
    <div class="footer-bottom">
      <div class="container">
        <div class="footer-bottom-inner">
          <p class="copyright">
            &copy; {{ currentYear }} M&R Filtermedien. Alle Rechte vorbehalten.
          </p>
          <nav class="footer-legal" aria-label="Rechtliche Hinweise">
            <RouterLink to="/impressum">Impressum</RouterLink>
            <RouterLink to="/datenschutz">Datenschutz</RouterLink>
          </nav>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.footer {
  background: var(--color-gray-900);
  color: var(--color-gray-400);
}

.footer-main {
  padding: var(--spacing-16) 0;
  border-bottom: 1px solid var(--color-gray-800);
}

.footer-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr 1.4fr;
  gap: var(--spacing-12);
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  text-decoration: none;
  margin-bottom: var(--spacing-4);
}

.footer-logo-img {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: var(--radius-sm);
}

.footer-logo-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-400);
}

.footer-tagline {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  line-height: var(--line-height-relaxed);
  max-width: 300px;
  margin: 0;
}

.footer-title {
  font-family: var(--font-family-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-400);
  letter-spacing: var(--letter-spacing-wider);
  text-transform: uppercase;
  margin-bottom: var(--spacing-4);
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.footer-links a {
  color: var(--color-gray-400);
  font-size: var(--font-size-sm);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.footer-links a:hover {
  color: var(--color-white);
}

.footer-contact {
  font-style: normal;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-relaxed);
}

.footer-contact p {
  margin-bottom: var(--spacing-3);
  color: var(--color-gray-400);
  max-width: none;
}

.footer-contact a {
  color: var(--color-gray-400);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.footer-contact a:hover {
  color: var(--color-primary-light);
}

.footer-hours {
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
}

/* Footer Bottom */
.footer-bottom {
  padding: var(--spacing-6) 0;
}

.footer-bottom-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyright {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  margin: 0;
}

.footer-legal {
  display: flex;
  gap: var(--spacing-6);
}

.footer-legal a {
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.footer-legal a:hover {
  color: var(--color-white);
}

/* Responsive */
@media (max-width: 1024px) {
  .footer-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-8);
  }

  .footer-bottom-inner {
    flex-direction: column;
    gap: var(--spacing-3);
    text-align: center;
  }
}
</style>
